// TennisTrack Hub — Form Crea/Modifica Template v2 | 029 Project
// Formato v2: sezioni libere + esercizi dalla biblioteca
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import Navbar from '../../components/Navbar';
import './Template.css';

// ── Fabbrica sezione vuota ──────────────────────────────────────────────────
const sezioneBianca = (nome = '') => ({
  nome: nome,
  esercizi: []
});

// ── Fabbrica esercizio vuoto in una sezione ─────────────────────────────────
const esercizioBianco = () => ({
  biblioteca_id: null,   // se scelto dalla biblioteca
  nome: '',              // nome libero (o quello preso dalla biblioteca)
  categoria: '',
  serie: '',
  ripetizioni: '',
  durata_min: '',
  note: ''
});

// ── Sezioni predefinite per tipo ────────────────────────────────────────────
const SEZIONI_DEFAULT_TENNIS  = ['Riscaldamento', 'Tecnica', 'Tattica', 'Defaticamento'];
const SEZIONI_DEFAULT_FITNESS = ['Riscaldamento', 'Forza', 'Cardio', 'Defaticamento'];

// ── Converte contenuto v1 → v2 ──────────────────────────────────────────────
const converti_v1_a_v2 = (contenuto, tipo) => {
  const esercizi = contenuto?.esercizi || [];
  const sez = sezioneBianca('Allenamento');
  sez.esercizi = esercizi.map(e => ({
    biblioteca_id: null,
    nome: e.colpo || e.nome || '',
    categoria: e.tipologia || '',
    serie: e.serie || '',
    ripetizioni: e.ripetizioni || '',
    durata_min: e.durata_min || '',
    note: e.descrizione || e.note || ''
  }));
  return { sezioni: [sez] };
};

// ============================================================
// MODAL PICKER BIBLIOTECA
// ============================================================
const ModalBiblioteca = ({ tipo, onSeleziona, onChiudi }) => {
  const [esercizi, setEsercizi]     = useState([]);
  const [categorie, setCategorie]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [cerca, setCerca]           = useState('');
  const [filtroCat, setFiltroCat]   = useState('');
  const [filtroLiv, setFiltroLiv]   = useState('');

  const carica = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // Filtro sport: tennis e fitness hanno categorie diverse; 'entrambi' va bene per tutti
      if (tipo === 'tennis')  params.set('sport', 'tennis');
      if (tipo === 'fitness') params.set('sport', 'fitness');
      if (filtroCat) params.set('categoria', filtroCat);
      if (filtroLiv) params.set('livello', filtroLiv);
      if (cerca.trim()) params.set('cerca', cerca.trim());

      const [resEs, resCat] = await Promise.all([
        api.get(`/esercizi?${params.toString()}`),
        api.get('/esercizi/categorie')
      ]);
      setEsercizi(resEs.data.esercizi || []);
      setCategorie(resCat.data.categorie || []);
    } catch {
      // ignora errori di rete nel modal
    } finally {
      setLoading(false);
    }
  }, [tipo, filtroCat, filtroLiv, cerca]);

  useEffect(() => { carica(); }, [carica]);

  return (
    <div className="modal-overlay" onClick={onChiudi}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>📚 Biblioteca Esercizi</h3>
          <button className="modal-close" onClick={onChiudi}>✕</button>
        </div>

        {/* Filtri */}
        <div className="modal-filtri">
          <input
            className="form-input"
            placeholder="🔍 Cerca esercizio..."
            value={cerca}
            onChange={e => setCerca(e.target.value)}
          />
          <select className="form-select" value={filtroCat} onChange={e => setFiltroCat(e.target.value)}>
            <option value="">Tutte le categorie</option>
            {categorie.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="form-select" value={filtroLiv} onChange={e => setFiltroLiv(e.target.value)}>
            <option value="">Tutti i livelli</option>
            <option value="principiante">Principiante</option>
            <option value="intermedio">Intermedio</option>
            <option value="avanzato">Avanzato</option>
          </select>
        </div>

        {/* Lista */}
        <div className="modal-lista">
          {loading && <div className="loading-msg">Caricamento...</div>}
          {!loading && esercizi.length === 0 && (
            <div className="empty-state" style={{ padding: '32px 16px' }}>
              <div className="empty-icon">🔍</div>
              <p>Nessun esercizio trovato.</p>
            </div>
          )}
          {!loading && esercizi.map(es => (
            <div
              key={es.id}
              className="bib-es-row"
              onClick={() => onSeleziona(es)}
            >
              <div className="bib-es-nome">{es.nome}</div>
              <div className="bib-es-meta">
                <span className="bib-tag">{es.categoria}</span>
                <span className="bib-tag bib-tag-livello">{es.livello}</span>
                {es.volume && <span className="bib-tag bib-tag-volume">{es.volume}</span>}
                {!es.ufficiale && <span className="bib-tag bib-tag-mio">✏️ Mio</span>}
              </div>
              {es.descrizione && (
                <div className="bib-es-desc">{es.descrizione}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// COMPONENTE PRINCIPALE
// ============================================================
const FormTemplate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isModifica = Boolean(id);

  const [tipo, setTipo]         = useState('tennis');
  const [titolo, setTitolo]     = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [livello, setLivello]   = useState('principiante');
  const [sezioni, setSezioni]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [errore, setErrore]     = useState('');
  const [suggerimento, setSuggerimento] = useState('');
  const [loadingEdit, setLoadingEdit] = useState(false);

  // Modal biblioteca: { sezioneIdx, esercizioIdx o null (aggiunta) }
  const [modalAperto, setModalAperto] = useState(null);

  // ── Inizializza sezioni default quando si crea nuovo template ──
  useEffect(() => {
    if (!isModifica) {
      const nomiDefault = tipo === 'tennis' ? SEZIONI_DEFAULT_TENNIS : SEZIONI_DEFAULT_FITNESS;
      setSezioni(nomiDefault.map(n => sezioneBianca(n)));
    }
  }, [tipo, isModifica]); // eslint-disable-line

  // ── Carica dati se modifica ──────────────────────────────────
  useEffect(() => {
    if (!isModifica) return;
    setLoadingEdit(true);
    api.get(`/template/miei/${id}`)
      .then(res => {
        const t = res.data.template;
        setTipo(t.tipo || 'tennis');
        setTitolo(t.titolo || '');
        setDescrizione(t.descrizione || '');
        setLivello(t.livello || 'principiante');

        const contenuto = typeof t.contenuto === 'string'
          ? JSON.parse(t.contenuto)
          : t.contenuto;

        const fv = t.formato_versione || 'v1';

        if (fv === 'v2' && Array.isArray(contenuto?.sezioni)) {
          setSezioni(contenuto.sezioni);
        } else {
          // v1 legacy → converti in v2
          const conv = converti_v1_a_v2(contenuto, t.tipo || 'tennis');
          setSezioni(conv.sezioni);
        }
      })
      .catch(() => setErrore('Errore nel caricamento del template.'))
      .finally(() => setLoadingEdit(false));
  }, [id, isModifica]);

  // ── Cambio tipo (reset sezioni) ──────────────────────────────
  const cambioTipo = (nuovoTipo) => {
    if (nuovoTipo === tipo) return;
    setTipo(nuovoTipo);
    const nomiDefault = nuovoTipo === 'tennis' ? SEZIONI_DEFAULT_TENNIS : SEZIONI_DEFAULT_FITNESS;
    setSezioni(nomiDefault.map(n => sezioneBianca(n)));
  };

  // ── Gestione sezioni ─────────────────────────────────────────
  const aggiungiSezione = () => {
    setSezioni(prev => [...prev, sezioneBianca('Nuova sezione')]);
  };

  const rimuoviSezione = (sIdx) => {
    if (sezioni.length <= 1) return;
    setSezioni(prev => prev.filter((_, i) => i !== sIdx));
  };

  const rinominaSezione = (sIdx, nome) => {
    setSezioni(prev => prev.map((s, i) => i === sIdx ? { ...s, nome } : s));
  };

  const spostaSezione = (sIdx, dir) => {
    setSezioni(prev => {
      const arr = [...prev];
      const target = sIdx + dir;
      if (target < 0 || target >= arr.length) return arr;
      [arr[sIdx], arr[target]] = [arr[target], arr[sIdx]];
      return arr;
    });
  };

  // ── Gestione esercizi nella sezione ─────────────────────────
  const aggiungiEsercizioBianco = (sIdx) => {
    setSezioni(prev => prev.map((s, i) =>
      i === sIdx ? { ...s, esercizi: [...s.esercizi, esercizioBianco()] } : s
    ));
  };

  const rimuoviEsercizio = (sIdx, eIdx) => {
    setSezioni(prev => prev.map((s, i) =>
      i === sIdx ? { ...s, esercizi: s.esercizi.filter((_, j) => j !== eIdx) } : s
    ));
  };

  const aggiornaEsercizio = (sIdx, eIdx, campo, valore) => {
    setSezioni(prev => prev.map((s, i) =>
      i === sIdx
        ? { ...s, esercizi: s.esercizi.map((e, j) => j === eIdx ? { ...e, [campo]: valore } : e) }
        : s
    ));
  };

  const spostaEsercizio = (sIdx, eIdx, dir) => {
    setSezioni(prev => prev.map((s, i) => {
      if (i !== sIdx) return s;
      const arr = [...s.esercizi];
      const target = eIdx + dir;
      if (target < 0 || target >= arr.length) return s;
      [arr[eIdx], arr[target]] = [arr[target], arr[eIdx]];
      return { ...s, esercizi: arr };
    }));
  };

  // ── Picker biblioteca ────────────────────────────────────────
  const apriModal = (sIdx) => {
    setModalAperto({ sIdx });
  };

  const selezionaEsercizioLibreria = (esercizioDb) => {
    if (modalAperto === null) return;
    const { sIdx } = modalAperto;
    const nuovoEs = {
      biblioteca_id: esercizioDb.id,
      nome: esercizioDb.nome,
      categoria: esercizioDb.categoria,
      serie: '',
      ripetizioni: esercizioDb.volume || '',
      durata_min: '',
      note: esercizioDb.descrizione || ''
    };
    setSezioni(prev => prev.map((s, i) =>
      i === sIdx ? { ...s, esercizi: [...s.esercizi, nuovoEs] } : s
    ));
    setModalAperto(null);
  };

  // ── Submit ───────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrore('');
    setSuggerimento('');
    if (!titolo.trim()) { setErrore('Il titolo è obbligatorio.'); return; }

    setLoading(true);
    const payload = {
      titolo: titolo.trim(),
      descrizione: descrizione.trim(),
      tipo,
      livello,
      formato_versione: 'v2',
      contenuto: { sezioni }
    };

    try {
      if (isModifica) {
        await api.put(`/template/miei/${id}`, payload);
        navigate(`/template/miei/${id}`);
      } else {
        const res = await api.post('/template/miei', payload);
        if (res.data.suggerimento) setSuggerimento(res.data.suggerimento);
        navigate('/template');
      }
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore nel salvataggio.');
    } finally {
      setLoading(false);
    }
  };

  // ── Loading edit ─────────────────────────────────────────────
  if (loadingEdit) {
    return (
      <div className="page-layout">
        <Navbar />
        <main className="page-main"><div className="loading-msg">Caricamento...</div></main>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────
  return (
    <div className="page-layout">
      <Navbar />
      <main className="page-main">

        {/* Header */}
        <div className="page-header">
          <div>
            <h1>{isModifica ? '✏️ Modifica Template' : '📋 Nuovo Template'}</h1>
            <p>{isModifica ? 'Aggiorna i dettagli del tuo template' : 'Crea un template riutilizzabile con sezioni personalizzate'}</p>
          </div>
          <button className="btn btn-secondary" onClick={() => navigate('/template')}>
            ← Indietro
          </button>
        </div>

        {errore && <div className="alert alert-error">{errore}</div>}
        {suggerimento && <div className="alert alert-info">💡 {suggerimento}</div>}

        <form className="card form-template" onSubmit={handleSubmit}>

          {/* Tipo */}
          <div className="form-group">
            <label className="form-label">Tipo di allenamento</label>
            <div className="tipo-selector">
              <button type="button" className={`tipo-btn ${tipo === 'tennis' ? 'active' : ''}`} onClick={() => cambioTipo('tennis')}>
                🎾 Tennis
              </button>
              <button type="button" className={`tipo-btn ${tipo === 'fitness' ? 'active' : ''}`} onClick={() => cambioTipo('fitness')}>
                💪 Fitness
              </button>
            </div>
          </div>

          {/* Titolo */}
          <div className="form-group">
            <label className="form-label">Titolo *</label>
            <input
              type="text"
              className="form-input"
              placeholder="Es. Allenamento base rovescio"
              value={titolo}
              onChange={e => setTitolo(e.target.value)}
              maxLength={120}
              required
            />
          </div>

          {/* Descrizione */}
          <div className="form-group">
            <label className="form-label">Descrizione (opzionale)</label>
            <textarea
              className="form-textarea"
              placeholder="Descrivi brevemente l'obiettivo del template..."
              value={descrizione}
              onChange={e => setDescrizione(e.target.value)}
              rows={3}
              maxLength={500}
            />
          </div>

          {/* Livello */}
          <div className="form-group">
            <label className="form-label">Livello</label>
            <select className="form-select" value={livello} onChange={e => setLivello(e.target.value)}>
              <option value="principiante">Principiante</option>
              <option value="intermedio">Intermedio</option>
              <option value="avanzato">Avanzato</option>
            </select>
          </div>

          {/* ── SEZIONI ── */}
          <div className="sezioni-section">
            <div className="sezioni-header">
              <h3>📂 Sezioni del template <span style={{ fontSize: '0.8rem', fontWeight: 400, color: 'var(--text-secondary)' }}>({sezioni.length})</span></h3>
              <button type="button" className="btn btn-secondary btn-sm" onClick={aggiungiSezione}>
                + Aggiungi sezione
              </button>
            </div>

            {sezioni.map((sez, sIdx) => (
              <div key={sIdx} className="sezione-block">
                {/* Header sezione */}
                <div className="sezione-toolbar">
                  <div className="sezione-nome-wrapper">
                    <span className="sezione-drag-icon">☰</span>
                    <input
                      className="sezione-nome-input"
                      value={sez.nome}
                      onChange={e => rinominaSezione(sIdx, e.target.value)}
                      placeholder="Nome sezione..."
                      maxLength={60}
                    />
                  </div>
                  <div className="sezione-azioni">
                    <button type="button" className="btn-icon" onClick={() => spostaSezione(sIdx, -1)} title="Sposta su" disabled={sIdx === 0}>↑</button>
                    <button type="button" className="btn-icon" onClick={() => spostaSezione(sIdx, 1)}  title="Sposta giù" disabled={sIdx === sezioni.length - 1}>↓</button>
                    {sezioni.length > 1 && (
                      <button type="button" className="btn-icon btn-icon-danger" onClick={() => rimuoviSezione(sIdx)} title="Rimuovi sezione">🗑</button>
                    )}
                  </div>
                </div>

                {/* Esercizi della sezione */}
                <div className="sezione-esercizi">
                  {sez.esercizi.length === 0 && (
                    <div className="sezione-vuota">Nessun esercizio. Aggiungine uno dalla biblioteca o manualmente.</div>
                  )}

                  {sez.esercizi.map((es, eIdx) => (
                    <div key={eIdx} className="es-riga">
                      <div className="es-riga-header">
                        <span className="esercizio-num">#{eIdx + 1}</span>
                        {es.biblioteca_id && <span className="bib-tag bib-tag-src">📚 Biblioteca</span>}
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>
                          <button type="button" className="btn-icon" onClick={() => spostaEsercizio(sIdx, eIdx, -1)} disabled={eIdx === 0} title="Su">↑</button>
                          <button type="button" className="btn-icon" onClick={() => spostaEsercizio(sIdx, eIdx, 1)} disabled={eIdx === sez.esercizi.length - 1} title="Giù">↓</button>
                          <button type="button" className="btn-icon btn-icon-danger" onClick={() => rimuoviEsercizio(sIdx, eIdx)} title="Rimuovi">🗑</button>
                        </div>
                      </div>

                      <div className="esercizio-grid">
                        <div className="form-group">
                          <label className="form-label">Nome esercizio *</label>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Es. Riscaldamento corsa laterale"
                            value={es.nome}
                            onChange={e => aggiornaEsercizio(sIdx, eIdx, 'nome', e.target.value)}
                            maxLength={100}
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Categoria</label>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Es. Riscaldamento"
                            value={es.categoria}
                            onChange={e => aggiornaEsercizio(sIdx, eIdx, 'categoria', e.target.value)}
                            maxLength={60}
                          />
                        </div>
                      </div>

                      <div className="esercizio-grid triple">
                        <div className="form-group">
                          <label className="form-label">Serie</label>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="3"
                            value={es.serie}
                            onChange={e => aggiornaEsercizio(sIdx, eIdx, 'serie', e.target.value)}
                            maxLength={20}
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Ripetizioni / Volume</label>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="10 / 5 min"
                            value={es.ripetizioni}
                            onChange={e => aggiornaEsercizio(sIdx, eIdx, 'ripetizioni', e.target.value)}
                            maxLength={30}
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Durata (min)</label>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="10"
                            value={es.durata_min}
                            onChange={e => aggiornaEsercizio(sIdx, eIdx, 'durata_min', e.target.value)}
                            maxLength={10}
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Note</label>
                        <textarea
                          className="form-textarea"
                          placeholder="Istruzioni, varianti, attrezzatura necessaria..."
                          rows={2}
                          value={es.note}
                          onChange={e => aggiornaEsercizio(sIdx, eIdx, 'note', e.target.value)}
                          maxLength={400}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pulsanti aggiungi esercizio */}
                <div className="sezione-add-es">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => apriModal(sIdx)}
                  >
                    📚 Scegli dalla biblioteca
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() => aggiungiEsercizioBianco(sIdx)}
                  >
                    + Aggiungi manualmente
                  </button>
                </div>
              </div>
            ))}

            <button
              type="button"
              className="btn btn-secondary"
              onClick={aggiungiSezione}
              style={{ marginTop: '8px' }}
            >
              + Aggiungi sezione
            </button>
          </div>

          {/* Submit */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/template')}>
              Annulla
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Salvataggio...' : isModifica ? '💾 Aggiorna template' : '💾 Crea template'}
            </button>
          </div>
        </form>

      </main>

      {/* Modal biblioteca */}
      {modalAperto !== null && (
        <ModalBiblioteca
          tipo={tipo}
          onSeleziona={selezionaEsercizioLibreria}
          onChiudi={() => setModalAperto(null)}
        />
      )}
    </div>
  );
};

export default FormTemplate;
